/**
 * HouseholdAccessAdmin — owner-only "Household & access" section for the
 * Settings page.
 *
 * Lists every known account (seeded + registered) via
 * GET /api/auth/admin/users and lets the owner grant a tier via
 * PATCH /api/auth/admin/users/tier — e.g. promote the wife's registered
 * login to `advised`/`owner` so the day-trading nav unlocks for her.
 *
 * Renders nothing unless the current user's capabilities say is_owner
 * (the backend enforces the same guard, this is just UI hygiene).
 */
import { useCallback, useEffect, useState } from 'react'
import { CheckCircle, Loader2, RefreshCw, Users, XCircle } from 'lucide-react'
import { api } from '@/lib/api'
import { useCapabilities, useIsOwnerTier } from '@/hooks/useCapabilities'

interface AdminUser {
  email: string
  name: string
  tier: string
  role: string
  is_seed: boolean
  is_active: boolean
}

const TIERS = ['free', 'planner', 'advised', 'owner', 'demo'] as const

const TIER_BADGE: Record<string, string> = {
  owner: 'badge-purple',
  advised: 'badge-blue',
  planner: 'badge-green',
  demo: 'badge-amber',
  free: 'badge-neutral',
}

interface RowStatus { kind: 'success' | 'error'; text: string }

export default function HouseholdAccessAdmin() {
  const isOwner = useIsOwnerTier()
  const capabilities = useCapabilities(s => s.capabilities)
  const fetchCapabilities = useCapabilities(s => s.fetch)

  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [applying, setApplying] = useState<Record<string, boolean>>({})
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({})

  // Shell normally fetches capabilities on mount; refetch defensively if
  // this section renders before that resolves.
  useEffect(() => {
    if (!capabilities) void fetchCapabilities()
  }, [capabilities, fetchCapabilities])

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await api.get<AdminUser[]>('/api/auth/admin/users')
      setUsers(Array.isArray(res.data) ? res.data : [])
    } catch {
      setLoadError('Could not load users — check your connection and permissions.')
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (isOwner) void loadUsers()
  }, [isOwner, loadUsers])

  const setRowMessage = useCallback((email: string, status: RowStatus | null) => {
    setRowStatus(prev => {
      const next = { ...prev }
      if (status) next[email] = status
      else delete next[email]
      return next
    })
  }, [])

  const applyTier = useCallback(async (u: AdminUser) => {
    const email = u.email
    const tier = drafts[email] ?? u.tier ?? 'free'
    setApplying(prev => ({ ...prev, [email]: true }))
    setRowMessage(email, null)
    try {
      const res = await api.patch<{ email?: string; tier?: string; detail?: string }>(
        '/api/auth/admin/users/tier',
        { email, tier },
      )
      const newTier = res.data?.tier ?? tier
      setUsers(prev => prev.map(x => (x.email === email ? { ...x, tier: newTier } : x)))
      setRowMessage(email, { kind: 'success', text: `Now '${newTier}'` })
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setRowMessage(email, { kind: 'error', text: detail ?? 'Update failed — try again' })
    }
    setApplying(prev => ({ ...prev, [email]: false }))
    setTimeout(() => setRowMessage(email, null), 5000)
  }, [drafts, setRowMessage])

  if (!isOwner) return null

  return (
    <section className="card" aria-labelledby="section-household-access">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <Users size={15} className="text-text-muted" aria-hidden />
        <h2 id="section-household-access" className="font-semibold text-text-primary text-sm">
          Household &amp; access
        </h2>
        <button
          type="button"
          onClick={() => void loadUsers()}
          disabled={loading}
          aria-label="Refresh user list"
          className="ml-auto text-xs px-2 py-1 rounded-md border border-border text-text-muted hover:bg-surface-hover transition-colors flex items-center gap-1.5 disabled:opacity-40"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      <div className="px-5 py-3 border-b border-border">
        <p className="text-xs text-text-muted">
          Advised or Owner unlocks the trading and signals pages (Trade Ideas, Predictions, Paper Lab extras).
        </p>
      </div>

      {loadError && (
        <div className="px-5 py-3 text-sm text-red border-b border-border">{loadError}</div>
      )}

      {!loadError && users.length === 0 && !loading && (
        <div className="px-5 py-4 text-sm text-text-muted">No users found.</div>
      )}

      {users.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[440px]">
            <thead>
              <tr className="text-left text-xs text-text-muted border-b border-border">
                <th className="px-3 py-2.5 font-medium">User</th>
                <th className="px-3 py-2.5 font-medium">Tier</th>
                <th className="px-3 py-2.5 font-medium">Change tier</th>
                <th className="px-3 py-2.5 font-medium hidden sm:table-cell">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map(u => {
                const email = u.email ?? ''
                const tier = (u.tier ?? 'free').toLowerCase()
                const draft = drafts[email] ?? tier
                const busy = applying[email] ?? false
                const status = rowStatus[email]
                return (
                  <tr key={email}>
                    <td className="px-3 py-3">
                      <p className="text-text-primary">{u.name ?? email}</p>
                      <p className="text-xs text-text-muted">
                        {email}
                        {u.is_seed ? ' · seeded' : ''}
                        {u.is_active === false ? ' · inactive' : ''}
                      </p>
                      {/* Mobile: the Status column is hidden below sm — echo it here. */}
                      {status && (
                        <span className={`sm:hidden mt-1 text-xs flex items-center gap-1 ${status.kind === 'success' ? 'text-green' : 'text-red'}`}>
                          {status.kind === 'success' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {status.text}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className={TIER_BADGE[tier] ?? 'badge-neutral'}>{tier}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={draft}
                          aria-label={`New tier for ${email}`}
                          onChange={e => setDrafts(prev => ({ ...prev, [email]: e.target.value }))}
                          className="bg-surface-hover border border-border rounded-md text-sm text-text-primary px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue"
                        >
                          {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => void applyTier(u)}
                          disabled={busy || draft === tier}
                          className="text-xs px-3 py-1.5 rounded-md border border-blue/30 text-blue hover:bg-blue/10 transition-colors flex items-center gap-1.5 disabled:opacity-40"
                        >
                          {busy && <Loader2 size={12} className="animate-spin" />}
                          Apply
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 hidden sm:table-cell">
                      {status && (
                        <span className={`text-xs flex items-center gap-1 ${status.kind === 'success' ? 'text-green' : 'text-red'}`}>
                          {status.kind === 'success' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {status.text}
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
